import { useState } from 'react';
import { Lightbulb, Pencil, X } from 'lucide-react';
import { useI18n } from '../lib/i18n';
import { api, ApiError } from '../lib/api';
import { formatUsd, localeUI } from '../lib/format';

export type ReconOverride = {
  rcvCents: number | null; deductibleCents: number | null; nonRecoverableDepreciationCents: number | null;
  note: string | null; editedByName: string | null; editedAt: string;
  calculated: { rcvCents: number; deductibleCents: number; nonRecoverableDepreciationCents: number };
};
export type Reconciliation = {
  override?: ReconOverride | null;
  rcvCents: number;
  deductibleCents: number;
  depreciationCents: number;
  depreciationRecoverable: boolean | null;
  nonRecoverableDepreciationCents: number;
  acvCents: number | null;
  receivedCents: number;
  feeChargedCents: number;
  feeRateBps: number;
  feeExpectedCents: number;
  paidContractorCents: number;
  paidHomeownerCents: number;
  /** El RCV no sale de la columna del claim: esa traía NUESTRO estimado. */
  rcvEraNuestroEstimado?: boolean;
  outstandingFromInsuranceCents: number;
  undistributedCents: number;
  /** > 0 = lo repartido supera lo que entró: hay un hecho contado dos veces. */
  reparteDeMasCents?: number;
  /** Payout de invoices EMITIDOS sin pagar: plata comprometida, todavía no entregada. */
  comprometidoCents?: number;
  comprometeDeMasCents?: number;
  /** > 0 = el seguro mandó más de lo que este RCV permite: el RCV está viejo. */
  rcvDesactualizadoCents?: number;
  rcvMinimoSegunLaPlataCents?: number;
  /** Suma de los cheques por los que ya se facturó. */
  facturadoContraChequesCents?: number;
  /** Deuda cargada que se descontará del próximo invoice (mig 0522). */
  pendingDeductionsCents?: number;
  pendingDeductions?: { count: number; reasons: string } | null;
  differenceCents: number;
};

/**
 * Reconciliación del claim, con la MISMA contabilidad del PDF del invoice (la correcta).
 * Son dos cuentas distintas y se muestran separadas:
 *   1) Recuperación del seguro: RCV − deducible − depreciación NO recuperable − cheques
 *      recibidos = lo que el seguro TODAVÍA debe (idéntico al bloque "Final Reconciliation"
 *      del PDF). La depreciación RECUPERABLE no se resta: llega como segundo cheque.
 *   2) Distribución: cheques recibidos − fee − pagado al CO − pagado al HO = sin repartir.
 * Compartido entre el tab Payments (ClaimDetail) y el panel lateral (ClaimPaymentsFull).
 */
/** Salta al nodo del tracker con ese ancla y lo hace destellar (ids `tl-<anchor>`). */
function jumpToTrackerNode(anchor: string) {
  const el = document.getElementById(`tl-${anchor}`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add('tl-flash');
  window.setTimeout(() => el.classList.remove('tl-flash'), 1900);
}

export function PaymentReconciliation({ recon, linkToTracker = false, claimId, onEdited }: {
  recon: Reconciliation | null; linkToTracker?: boolean;
  /** Con claimId se habilita corregir a mano los campos que lee la IA. */
  claimId?: string; onEdited?: () => void;
}) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ rcv: '', ded: '', dep: '', note: '' });
  const [busy, setBusy] = useState(false);
  if (!recon) return null;

  const ov = recon.override ?? null;
  const abrirEdicion = () => {
    // Se precargan los valores VIGENTES (corregidos o calculados): editar es ajustar lo que se ve.
    setF({
      rcv: String(recon.rcvCents / 100),
      ded: String(recon.deductibleCents / 100),
      dep: String(recon.nonRecoverableDepreciationCents / 100),
      note: ov?.note ?? '',
    });
    setEditing(true);
  };
  const guardar = async (limpiar = false) => {
    if (!claimId) return;
    setBusy(true);
    try {
      const num = (v: string) => { const x = Number(String(v).replace(/[^0-9.]/g, '')); return Number.isFinite(x) ? x : 0; };
      await api.post(`/api/claims/${claimId}/reconciliation/override`, limpiar ? {} : {
        rcv: num(f.rcv), deductible: num(f.ded), nonRecoverableDepreciation: num(f.dep),
        note: f.note.trim() || null,
      });
      setEditing(false); onEdited?.();
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  };

  // Claims sin estimado aprobado (RCV en 0, típico de los importados de Monday): el cuadro se
  // muestra IGUAL en $0 — pedido del dueño: todos los claims deben tener su resumen. En vez de
  // gritar "sobrepago" (0 − cheques = negativo), se avisa que falta cargar el estimado.
  const noEstimate = recon.rcvCents <= 0;
  const owed = recon.outstandingFromInsuranceCents;
  const fullyCollected = owed <= 100; // ≤ $1 = ya llegó todo
  const left = recon.undistributedCents;
  const distributed = Math.abs(left) < 100; // < $1 = todo repartido
  const recoverablePending =
    recon.depreciationRecoverable === true && recon.depreciationCents > 0 && !fullyCollected;

  const Row = ({ label, value, sub, subRed, strong, jumpTo }: { label: string; value: number; sub?: string; subRed?: boolean; strong?: boolean; jumpTo?: string }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 'var(--fs-xs)', padding: '3px 0' }}>
      {/* Con jumpTo el label es un link que baja al pago en el tracker (para ver el comprobante). */}
      {jumpTo && linkToTracker && value !== 0 ? (
        <button
          className="btn ghost sm"
          style={{ padding: 0, border: 0, background: 'none', fontSize: 'var(--fs-xs)', color: 'var(--accent)', textDecoration: 'underline', textUnderlineOffset: 2, cursor: 'pointer' }}
          title={t('rec.jumpHelp')}
          onClick={() => jumpToTrackerNode(jumpTo)}
        >
          {label} ↓
        </button>
      ) : (
        <span className={strong ? undefined : 'muted'} style={strong ? { fontWeight: 600 } : undefined}>
          {label}{sub ? <span style={subRed ? { color: 'var(--red)', fontWeight: 700 } : { opacity: 0.7 }}> · {sub}</span> : ''}
        </span>
      )}
      <span style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>{value < 0 ? '−' : ''}{formatUsd(Math.abs(value))}</span>
    </div>
  );
  const Total = ({ label, value, tone, note }: { label: string; value: number; tone: 'green' | 'amber' | 'red'; note: string }) => (
    <>
      <div style={{ borderTop: '2px solid var(--border, #e5e7eb)', margin: '6px 0 4px' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }}>
        <span>{label}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', color: tone === 'green' ? 'var(--green, #16a34a)' : tone === 'amber' ? 'var(--orange)' : 'var(--red, #dc2626)' }}>
          {value < 0 ? '−' : ''}{formatUsd(Math.abs(value))}
        </span>
      </div>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 6 }}>{note}</div>
    </>
  );

  return (
    /* Los dos cuadros (lo que falta cobrar · lo que se repartió) se leen de a pares: uno al lado
       del otro cuando el contenedor da el ancho, apilados cuando no (auto-fit mira el CONTENEDOR,
       así que en el drawer angosto siguen uno debajo del otro). Apilados siempre, sumaban 800px
       de alto para 8 renglones de números. */
    <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', alignItems: 'start' }}>
      {/* 1) RECUPERACIÓN DEL SEGURO — mismo cuadro que el PDF del invoice */}
      <div style={{ border: '1px solid var(--border, #e5e7eb)', borderRadius: 10, padding: 14, background: 'var(--surface-2, #f8fafc)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], fontSize: 'var(--fs-sm)' }}>{t('rec.recoveryTitle')}</span>
          {/* Marca de corrección a mano: quién y cuándo. Sin esto, un número editado se ve igual
              que uno calculado y nadie sabe a qué atenerse. */}
          {ov && (
            <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'rgba(37,99,235,.12)', color: 'var(--info)' }}
              title={ov.note ?? ''}>
              <Pencil size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('rec.editedBy', { who: ov.editedByName ?? '—', when: new Date(ov.editedAt).toLocaleDateString(localeUI()) })}
            </span>
          )}
          <span style={{ flex: 1 }} />
          {claimId && !editing && (
            <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={abrirEdicion}><Pencil size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('rec.edit')}</button>
          )}
        </div>

        {editing && claimId && (
          <div style={{ display: 'grid', gap: 7, marginBottom: 10, padding: 10, borderRadius: 8, background: 'var(--card)', border: '1px solid var(--line)' }}>
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('rec.editHint')}</div>
            {([
              ['rcv', t('rec.rcv'), ov?.calculated.rcvCents ?? recon.rcvCents],
              ['ded', t('rec.deductible'), ov?.calculated.deductibleCents ?? recon.deductibleCents],
              ['dep', t('rec.nonRecovDepr'), ov?.calculated.nonRecoverableDepreciationCents ?? recon.nonRecoverableDepreciationCents],
            ] as const).map(([k, label, calc]) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-xs)' }}>
                <span style={{ flex: 1 }}>{label}</span>
                {/* Lo que decía el cálculo, al lado: es la referencia para saber si vale la pena tocarlo. */}
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('rec.calcWas', { v: formatUsd(calc) })}</span>
                <input className="input sm" style={{ width: 120, textAlign: 'right' }} inputMode="decimal"
                  value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
              </label>
            ))}
            <input className="input sm" placeholder={t('rec.whyPh')} value={f.note}
              onChange={(e) => setF({ ...f, note: e.target.value })} />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button className="btn sm" disabled={busy} onClick={() => void guardar(false)}>{t('common.save')}</button>
              {ov && <button className="btn ghost sm" disabled={busy} onClick={() => void guardar(true)}>{t('rec.resetCalc')}</button>}
              <span style={{ flex: 1 }} />
              <button className="btn ghost sm" disabled={busy} onClick={() => setEditing(false)}><X size={14} strokeWidth={1.75} /></button>
            </div>
          </div>
        )}

        <Row label={t('rec.rcv')} value={recon.rcvCents} strong />
        {/* El RCV de la tarjeta del claim traía NUESTRO estimado (una demanda, no una aprobación)
            y acá se usa el del seguro. Se dice en voz alta: si no, el número de la tarjeta y el de
            este cuadro no coinciden y nadie sabe cuál creer (Deweese / Erika, 2026-10-20). */}
        {recon.rcvEraNuestroEstimado && (
          <div style={{ marginTop: 2, marginBottom: 2, fontSize: 'var(--fs-2xs)', color: 'var(--orange)' }}>
            <Lightbulb size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('rec.rcvDelSeguro')}
          </div>
        )}
        <div style={{ borderTop: '1px dashed var(--border, #e5e7eb)', margin: '4px 0' }} />
        <Row label={`(−) ${t('rec.deductible')}`} value={-recon.deductibleCents} />
        <Row label={`(−) ${t('rec.nonRecovDepr')}`} value={-recon.nonRecoverableDepreciationCents} />
        <Row label={`(−) ${t('rec.checksReceived')}`} value={-recon.receivedCents} />
        {noEstimate ? (
          <Total label={t('rec.noEstimate')} value={0} tone="amber" note={t('rec.noEstimateNote')} />
        ) : fullyCollected ? (
          <Total label={t('rec.fullyCollected')} value={Math.max(0, owed)} tone="green" note={t('rec.fullyCollectedNote')} />
        ) : (
          <Total label={t('rec.stillOwed')} value={owed} tone={owed < 0 ? 'red' : 'amber'} note={owed < 0 ? t('rec.overpaidNote') : t('rec.stillOwedNote')} />
        )}
        {/* La depreciación recuperable NO se resta: es exactamente el cheque que falta. */}
        {recoverablePending && (
          <div style={{ marginTop: 6, fontSize: 'var(--fs-2xs)', color: 'var(--orange)' }}>
            <Lightbulb size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('rec.recoverableHint', { amount: formatUsd(recon.depreciationCents) })}
          </div>
        )}
      </div>

      {/* 2) DISTRIBUCIÓN del dinero que YA entró */}
      <div style={{ border: '1px solid var(--border, #e5e7eb)', borderRadius: 10, padding: 14, background: 'var(--surface-2, #f8fafc)' }}>
        <div style={{ fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], fontSize: 'var(--fs-sm)', marginBottom: 8 }}>{t('rec.distributionTitle')}</div>
        <Row label={t('rec.checksReceived')} value={recon.receivedCents} strong />
        <div style={{ borderTop: '1px dashed var(--border, #e5e7eb)', margin: '4px 0' }} />
        {/* El fee muestra el % del contrato que se está cobrando. */}
        <Row label={`(−) ${t('rec.fee')} (${recon.feeRateBps / 100}%)`} value={-recon.feeChargedCents} />
        <Row label={`(−) ${t('rec.paidCo')}`} value={-recon.paidContractorCents} jumpTo="paid-contractor" />
        <Row label={`(−) ${t('rec.paidHo')}`} value={-recon.paidHomeownerCents} jumpTo="paid-homeowner" />
        {/* LO COMPROMETIDO. Los tres renglones de arriba son plata que YA SALIÓ; un invoice recién
            emitido no mueve ninguno, así que el cuadro no tenía nada que decir justo cuando hace
            falta: al facturar. Este renglón es el payout emitido y todavía sin pagar. */}
        {(recon.comprometidoCents ?? 0) > 0 && (
          <Row label={`(−) ${t('rec.committed')}`} value={-(recon.comprometidoCents ?? 0)} />
        )}
        {distributed ? (
          <Total label={t('rec.allDistributed')} value={0} tone="green" note={t('rec.allDistributedNote')} />
        ) : (
          <Total label={left > 0 ? t('rec.toDistribute') : t('rec.overDistributed')} value={left} tone={left > 0 ? 'amber' : 'red'} note={left > 0 ? t('rec.toDistributeNote') : t('rec.overDistributedNote')} />
        )}
        {/* NO SE PUEDE REPARTIR MÁS DE LO QUE ENTRÓ. Cuando la cuenta lo rompe, hay un hecho
            contado dos veces —el cheque que se llevó un lado Y un pago que describe la misma
            entrega— y se dice con todas las letras. Antes la pantalla mostraba el número inflado
            sin más: "Paid to homeowner $21.012,62" con $12.588,87 recibidos (Shanterrie Johns). */}
        {/* EL RCV VIEJO. Va PRIMERO porque suele ser la CAUSA de los otros dos carteles: si el
            seguro mandó más de lo que el RCV permite, "queda sin repartir" y "paga de más" son
            síntomas de que el número de arriba está desactualizado, no de un pago mal cargado.
            Alejandro Jiménez / Erika 2026-10-20: la pantalla decía "fully collected" y "still to
            distribute $1.067,14" al mismo tiempo y no sacaba la conclusión. */}
        {(recon.rcvDesactualizadoCents ?? 0) > 0 && (
          <div style={{ marginTop: 10, padding: 10, borderRadius: 8,
                        background: 'color-mix(in srgb, var(--orange) 8%, transparent)',
                        border: '1px solid var(--orange)', fontSize: 'var(--fs-2xs)' }}>
            <strong style={{ color: 'var(--orange)' }}>⚠ {t('rec.rcvViejo')}</strong>
            <div style={{ marginTop: 4 }}>
              {t('rec.rcvViejoNota', {
                falta: formatUsd(recon.rcvDesactualizadoCents ?? 0),
                minimo: formatUsd(recon.rcvMinimoSegunLaPlataCents ?? 0),
              })}
            </div>
          </div>
        )}
        {/* Y el segundo invariante: entregado + COMPROMETIDO por encima de lo que entró. Se
            enciende mientras el pago está en la cola, no después de que salió. Si ya se repartió
            de más, ese cartel (abajo) es el que manda y este sobra. */}
        {(recon.comprometeDeMasCents ?? 0) > 0 && (recon.reparteDeMasCents ?? 0) === 0 && (
          <div style={{ marginTop: 10, padding: 10, borderRadius: 8,
                        background: 'color-mix(in srgb, var(--red) 8%, transparent)',
                        border: '1px solid var(--red)', fontSize: 'var(--fs-2xs)' }}>
            <strong style={{ color: 'var(--red)' }}>⚠ {t('rec.comprometeDeMas')}</strong>
            <div style={{ marginTop: 4 }}>
              {t('rec.comprometeDeMasNota', { monto: formatUsd(recon.comprometeDeMasCents ?? 0) })}
            </div>
          </div>
        )}
        {(recon.reparteDeMasCents ?? 0) > 0 && (
          <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: 'var(--red-soft, #fee2e2)',
                        border: '1px solid var(--red, #dc2626)', fontSize: 'var(--fs-2xs)' }}>
            <strong style={{ color: 'var(--red, #dc2626)' }}>⚠ {t('rec.cuentaNoCuadra')}</strong>
            <div style={{ marginTop: 4 }}>
              {t('rec.cuentaNoCuadraNota', { monto: formatUsd(recon.reparteDeMasCents ?? 0) })}
            </div>
          </div>
        )}
        {/* LO QUE NOS DEBEN Y TODAVÍA NO SE DESCONTÓ. Se aplica solo al próximo invoice del claim,
            pero si no hay uno a la vista la deuda se queda esperando sin que nadie la vea — y una
            deuda invisible es una deuda perdida. El caso: el claim 1790 pagó $4.644,82 de más en
            agosto y estuvo dos meses sin que nadie lo notara. */}
        {(recon.pendingDeductionsCents ?? 0) > 0 && (
          <div style={{ marginTop: 10, padding: 10, borderRadius: 8,
                        background: 'color-mix(in srgb, var(--orange) 10%, transparent)',
                        border: '1px solid var(--orange)', fontSize: 'var(--fs-2xs)' }}>
            <strong style={{ color: 'var(--orange)' }}>
              {t('rec.deudaPendiente', { monto: formatUsd(recon.pendingDeductionsCents ?? 0) })}
            </strong>
            <div style={{ marginTop: 4 }}>
              {t('rec.deudaPendienteNota')}
              {recon.pendingDeductions?.reasons ? ` — ${recon.pendingDeductions.reasons}` : ''}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
